<template>
  <PageTemplate
    :page-name="'login'"
    :is-loading="isTokenLoading"
  >
    <FormContainer class="login__form-container">
      <div class="login__text-block">
        <h2 class="login__title">З поверненням!</h2>
        <p class="login__description"> Зосереджуйся на головному — розвитку і прибутку разом з UConnect </p>
      </div>
      <form class="login__form" @submit.prevent="handleSubmit">
        <LabeledTextInput
          class="login-form__input--email"
          label="Email"
          placeholder="example@email.com"
          v-model="form.email"
          :error-messages="errors.email ? [errors.email] : []"
          @input="clearError('email')"
        />
        <LabeledTextInput
          class="login-form__input--password"
          label="Пароль"
          placeholder="*********"
          type="password"
          v-model="form.password"
          :error-messages="errors.password ? [errors.password] : []"
          @input="clearError('password')"
        />
        <div class="login-form__forgot-password">
          <SmartLink
            class="login-form__link--forgot-password"
            :route="{ name: 'forgot-password' }"
          >
            Забув пароль?
          </SmartLink>
        </div>
        <TurnstileWidget
          ref="turnstile"
          @token-received="handleTurnstileSuccess"
          @token-expired="handleTurnstileExpired"
        />
        <div class="login-form__login-button">
          <OrangeFancyButton
            class="login-form__controls--login"
            type="submit"
            :disabled="!(form.email && form.password && cfToken)"
          >
            <SpinnerLoader
              v-show="isLoadingUserData"
              class="login-form__controls--loader"
              color="#FFFFFF"
            />
            Увійти
          </OrangeFancyButton>
          <p
            v-show="loginError"
            class="login-form__login-button__error-text"
          >
            {{ loginError }}
          </p>
        </div>
        <div class="login-form__register">
          <p class="login-form__register-text">Ще немає акаунту?</p>
          <SmartLink
            class="login-form__link--register"
            :route="{ name: 'registration' }"
          >
            Зареєструйся
          </SmartLink>
        </div>
        <TelegramLoginButton />
      </form>
    </FormContainer>
  </PageTemplate>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import TelegramLoginButton from '../telegram-login-button';
import { FormContainer, PageTemplate } from '@/shared/ui/account';
import { LabeledTextInput, OrangeFancyButton } from '@/shared/ui/form';
import { SmartLink } from '@/shared/ui/link';
import { SpinnerLoader } from '@/shared/ui/loader';
import TurnstileWidget from '@/shared/ui/turnstile/TurnstileWidget';

export default {
  data() {
    return {
      isTokenLoading: false,
      loginError: '',
      cfToken: '',
      form: {
        email: '',
        password: '',
      },
      errors: {
        email: '',
        password: '',
      },
    };
  },
  components: {
    PageTemplate, FormContainer, LabeledTextInput, SmartLink, OrangeFancyButton, SpinnerLoader, TelegramLoginButton,
    TurnstileWidget
  },
  computed: {
    ...mapGetters('loginModule', [
      'isLoadingUserData', 'isUserLoggedIn', 'getUserInfo', 'getAccessToken'
    ]),
  },
  created() {
    const token = this.$route.query.token;

    if (!token) {
      return;
    }

    this.isTokenLoading = true;
    this.LOGIN_WITH_TOKEN(token)
      .then(() => {
        this.$router.replace(this.$route.query.redirect || '/prime');
      })
      .catch(() => {
        this.isTokenLoading = false;
      });
  },
  methods: {
    ...mapActions(['LOGIN_WITH_TOKEN']),
    validateEmail(email) {
      if (!email || email.trim().length === 0) {
        return 'Email є обов\'язковим';
      }
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(email)) {
        return 'Введіть коректну email адресу';
      }
      return '';
    },
    validatePassword(password) {
      if (!password || password.length === 0) {
        return 'Пароль є обов\'язковим';
      }
      return '';
    },
    validateForm() {
      this.errors.email = this.validateEmail(this.form.email);
      this.errors.password = this.validatePassword(this.form.password);

      return !this.errors.email && !this.errors.password;
    },
    clearError(field) {
      this.errors[field] = '';
      this.loginError = '';
    },
    handleTurnstileSuccess(token) {
      this.cfToken = token;
    },
    handleTurnstileExpired() {
      this.cfToken = '';
    },
    resetTurnstile() {
      this.cfToken = '';
      if (this.$refs.turnstile) {
        this.$refs.turnstile.reset();
      }
    },
    async handleSubmit() {
      if (!this.validateForm()) {
        return;
      }

      this.loginError = '';

      try {
        await this.$store.dispatch('LOGIN', {
          email: this.form.email,
          password: this.form.password,
          cf_token: this.cfToken,
        });
        this.$router.push({ name: 'prime' });
      } catch (error) {
        // Turnstile-токен одноразовий: після невдалої спроби потрібен новий
        this.resetTurnstile();

        const status = error?.response?.status || 0;

        if (status === 401 || status === 403) {
          this.loginError = 'Неправильний email або пароль';
        } else if (status >= 500) {
          this.loginError = 'Внутрішня помилка сервера. Спробуйте пізніше.';
        } else {
          this.loginError = 'Не вдалося увійти. Перевірте з\'єднання та спробуйте ще раз.';
        }
      }
    },
  },
  watch: {
    isUserLoggedIn: {
      immediate: false,
      handler(loggedIn) {
        if (loggedIn) {
          this.$store.commit('AUTH_SUCCESS', { token: this.getAccessToken, info: this.getUserInfo });
          this.$router.push({ name: 'prime' });
        }
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.login {
  &__form-container {
    margin: 170px auto 0;
    width: 600px;

    @media (max-width: 630px) {
      margin: 50px 16px 0;
      width: auto;
    }
  }

  &__title {
    font-family: "IBM Plex Sans", sans-serif;
    font-size: 36px;
    font-weight: 400;
    color: #0C1421;
    text-align: center;
    margin-bottom: 16px;

    @media (max-width: 630px) {
      font-size: 24px;
    }
  }

  &__description {
    font-size: 18px;
    font-weight: 400;
    color: #313957;
    text-align: center;

    @media (max-width: 630px) {
      font-size: 14px;
    }
  }
}

.login-form {
  &__link {
    &--forgot-password, &--register {
      font-family: 'Inter', sans-serif;
      font-size: 16px;
      font-weight: 400;
      color: #6171E4;

      &:hover {
        text-decoration: underline;
      }
    }
  }

  &__input {
    &--email, &--password {
      margin-bottom: 16px;
    }
  }

  &__forgot-password {
    display: flex;
    justify-content: flex-end;
    margin-bottom: 16px;
  }

  &__login-button {
    margin-bottom: 16px;

    &__error-text {
      font-family: "Inter", sans-serif;
      font-size: 12px;
      color: #f00;
      margin-top: 4px;
      margin-bottom: 12px;
    }
  }

  &__register {
    display: flex;
    justify-content: center;
  }

  &__register-text {
    margin-right: 8px;
  }

  &__controls {
    &--loader {
      margin-right: 12px;
    }
  }
}

::v-deep .app-header {
  justify-content: center;
}

::v-deep .input-label,
.input-label {
  position: relative;
  font-family: 'Inter', sans-serif;
  font-size: 18px;
  font-weight: 400;
  color: #494E53;
  margin-bottom: 8px;
}

::v-deep .input-text,
.input-text {
  position: relative !important;
  font-family: 'Inter', sans-serif !important;
  font-size: 22px !important;
  font-weight: 400 !important;
  background-color: #EBF2FF !important;
  width: 515px !important;
  border-radius: 16px !important;
  border: none !important;
  color: #494E53 !important;
  padding: 16px 24px !important;

  @media (max-width: 1300px) {
    width: 100% !important;
  }

  &::placeholder {
    color: #8E9DB7 !important;
  }

  &--has-error {
    border: 2px solid #FF3B30 !important;
  }
}

::v-deep .orange-fancy-button__content {
  display: flex;
  justify-content: center;
}
</style>
